import {
  Progress as NextUIProgress,
  ProgressProps as NextUIProgressProps,
} from "@heroui/react";
import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import usePasswordStrength, {
  MAX,
  MEDIUM,
  STRONG,
  VERY_STRONG,
  VERY_WEAK,
  WEAK,
} from "dsl/hooks/usePasswordStrength";
import { useEffect, useMemo } from "react";
import { useTranslations } from "translations";

type Color = Extract<
  NextUIProgressProps["color"],
  "success" | "warning" | "danger"
>;

export type PasswordIndicatorProps = Omit<NextUIProgressProps, "color"> & {
  id?: string;
  password: string;
  ref?: React.Ref<HTMLDivElement>;
};

type PasswordStrength = {
  color: Color;
  valueLabel: string;
};

export function PasswordIndicator({ ref, ...props }: PasswordIndicatorProps) {
  const translations = useTranslations();
  const { strength } = usePasswordStrength(props.password);
  const { updateA11yMessage } = useA11yMessage();

  const { color, valueLabel }: PasswordStrength = useMemo(() => {
    switch (strength) {
      case VERY_STRONG:
        return {
          color: "success",
          valueLabel: translations.login.loginPage.passwordVeryStrong,
        };
      case STRONG:
        return {
          color: "success",
          valueLabel: translations.login.loginPage.passwordStrong,
        };
      case MEDIUM:
        return {
          color: "warning",
          valueLabel: translations.login.loginPage.passwordMedium,
        };
      case WEAK:
        return {
          color: "danger",
          valueLabel: translations.login.loginPage.passwordWeak,
        };
      case VERY_WEAK:
      default:
        return {
          color: "danger",
          valueLabel: translations.login.loginPage.passwordVeryWeak,
        };
    }
  }, [strength, translations]);

  useEffect(() => {
    updateA11yMessage(valueLabel);
  }, [valueLabel]);

  return (
    <NextUIProgress
      ref={ref}
      {...props}
      aria-label={translations.login.loginPage.passwordIndicatorAriaLabel}
      classNames={{
        base: "gap-1",
        label: "text-large",
        labelWrapper: "order-1 self-end",
        track: "bg-gray-400",
        value: "text-xs",
      }}
      color={color}
      maxValue={MAX}
      showValueLabel={true}
      size="sm"
      value={strength}
      valueLabel={valueLabel}
    />
  );
}

PasswordIndicator.displayName = "RecareUI.PasswordIndicator";
